import React, { useState, useEffect } from 'react';
import { format, subDays, addHours, subHours } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const PlanningTimeline = ({ timeline = [], barbecueDate, barbecueTime, onUpdate }) => {
  const [tasks, setTasks] = useState([]);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    assignedTo: '',
    category: 'preparation',
    completed: false,
    priority: 'medium'
  });

  const [viewMode, setViewMode] = useState('timeline'); // timeline, calendar, list
  const [showAddForm, setShowAddForm] = useState(false);

  useEffect(() => {
    setTasks(timeline);
  }, [timeline]);

  useEffect(() => {
    if (barbecueDate && barbecueTime && tasks?.length === 0) {
      generateDefaultTimeline();
    }
  }, [barbecueDate, barbecueTime]);

  const categories = [
    { value: 'shopping', label: 'Compras', icon: 'ShoppingCart', color: 'text-blue-600' },
    { value: 'preparation', label: 'Preparação', icon: 'ChefHat', color: 'text-orange-600' },
    { value: 'setup', label: 'Montagem', icon: 'Settings', color: 'text-purple-600' },
    { value: 'cooking', label: 'Cozimento', icon: 'Flame', color: 'text-red-600' },
    { value: 'cleanup', label: 'Limpeza', icon: 'Trash2', color: 'text-green-600' }
  ];

  const priorities = [
    { value: 'high', label: 'Alta', color: 'bg-red-100 text-red-800' },
    { value: 'medium', label: 'Média', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'low', label: 'Baixa', color: 'bg-green-100 text-green-800' }
  ];

  const generateDefaultTimeline = () => {
    if (!barbecueDate || !barbecueTime) return;

    const eventDateTime = new Date(`${barbecueDate}T${barbecueTime}`);
    
    const defaultTasks = [
      // 3 dias antes
      {
        id: Date.now() + 1,
        title: 'Confirmar participantes',
        description: 'Enviar lista final e confirmar presença',
        date: format(subDays(eventDateTime, 3), 'yyyy-MM-dd'),
        time: '18:00',
        category: 'preparation',
        priority: 'high',
        assignedTo: '',
        completed: false
      },
      // 2 dias antes
      {
        id: Date.now() + 2,
        title: 'Fazer compras da carne',
        description: 'Comprar carnes no açougue escolhido',
        date: format(subDays(eventDateTime, 2), 'yyyy-MM-dd'),
        time: '14:00',
        category: 'shopping',
        priority: 'high',
        assignedTo: '',
        completed: false
      },
      // 1 dia antes
      {
        id: Date.now() + 3,
        title: 'Comprar bebidas e acompanhamentos',
        description: 'Cerveja, refrigerante, carvão, etc.',
        date: format(subDays(eventDateTime, 1), 'yyyy-MM-dd'),
        time: '16:00',
        category: 'shopping',
        priority: 'medium',
        assignedTo: '',
        completed: false
      },
      {
        id: Date.now() + 4,
        title: 'Preparar temperos',
        description: 'Preparar temperos e marinadas',
        date: format(subDays(eventDateTime, 1), 'yyyy-MM-dd'),
        time: '19:00',
        category: 'preparation',
        priority: 'medium',
        assignedTo: '',
        completed: false
      },
      // Dia do evento - antes
      {
        id: Date.now() + 5,
        title: 'Chegar no local',
        description: 'Chegar e organizar o espaço',
        date: barbecueDate,
        time: format(subHours(eventDateTime, 2), 'HH:mm'),
        category: 'setup',
        priority: 'high',
        assignedTo: '',
        completed: false
      },
      {
        id: Date.now() + 6,
        title: 'Montar churrasqueira',
        description: 'Acender o carvão e preparar a grelha',
        date: barbecueDate,
        time: format(subHours(eventDateTime, 1.5), 'HH:mm'),
        category: 'setup',
        priority: 'high',
        assignedTo: '',
        completed: false
      },
      {
        id: Date.now() + 7,
        title: 'Começar a assar',
        description: 'Iniciar o preparo das carnes',
        date: barbecueDate,
        time: format(subHours(eventDateTime, 1), 'HH:mm'),
        category: 'cooking',
        priority: 'high',
        assignedTo: '',
        completed: false
      },
      // Durante o evento
      {
        id: Date.now() + 8,
        title: 'Servir o churrasco',
        description: 'Momento de aproveitar!',
        date: barbecueDate,
        time: barbecueTime,
        category: 'cooking',
        priority: 'high',
        assignedTo: '',
        completed: false
      },
      // Após o evento
      {
        id: Date.now() + 9,
        title: 'Limpeza e organização',
        description: 'Limpar churrasqueira e organizar o local',
        date: barbecueDate,
        time: format(addHours(eventDateTime, 3), 'HH:mm'),
        category: 'cleanup',
        priority: 'medium',
        assignedTo: '',
        completed: false
      }
    ];

    setTasks(defaultTasks);
    onUpdate(defaultTasks);
  };

  const addTask = () => {
    if (!newTask?.title?.trim()) return;
    
    const task = {
      id: Date.now(),
      ...newTask,
      title: newTask?.title?.trim()
    };
    
    const updated = [...tasks, task]?.sort((a, b) => {
      const dateA = new Date(`${a?.date}T${a?.time}`);
      const dateB = new Date(`${b?.date}T${b?.time}`);
      return dateA - dateB;
    });
    
    setTasks(updated);
    onUpdate(updated);
    
    setNewTask({
      title: '',
      description: '',
      date: '',
      time: '',
      assignedTo: '',
      category: 'preparation',
      completed: false,
      priority: 'medium'
    });
    setShowAddForm(false);
  };

  const updateTask = (id, field, value) => {
    const updated = tasks?.map(task => 
      task?.id === id ? { ...task, [field]: value } : task
    );
    setTasks(updated);
    onUpdate(updated);
  };

  const removeTask = (id) => {
    const updated = tasks?.filter(task => task?.id !== id);
    setTasks(updated);
    onUpdate(updated);
  };

  const getCategoryInfo = (category) => {
    return categories?.find(c => c?.value === category) || categories?.[0];
  };

  const getPriorityInfo = (priority) => {
    return priorities?.find(p => p?.value === priority) || priorities?.[1];
  };

  const getTasksByDay = () => {
    const grouped = {};
    tasks?.forEach(task => {
      const date = task?.date;
      if (!grouped?.[date]) {
        grouped[date] = [];
      }
      grouped?.[date]?.push(task);
    });
    
    // Sort tasks within each day by time
    Object.keys(grouped)?.forEach(date => {
      grouped?.[date]?.sort((a, b) => a?.time?.localeCompare(b?.time));
    });
    
    return grouped;
  };

  const completedCount = tasks?.filter(task => task?.completed)?.length;
  const upcomingTasks = tasks?.filter(task => {
    const taskDate = new Date(`${task?.date}T${task?.time}`);
    const now = new Date();
    return taskDate > now && !task?.completed;
  });

  const renderTimeline = () => {
    const groupedTasks = getTasksByDay();
    const sortedDates = Object.keys(groupedTasks)?.sort();
    
    return (
      <div className="space-y-6">
        {sortedDates?.map((date) => {
          const dayTasks = groupedTasks?.[date];
          const parsedDate = new Date(date);
          const isToday = format(new Date(), 'yyyy-MM-dd') === date;
          const isEventDay = date === barbecueDate;
          
          return (
            <div key={date} className="relative">
              {/* Date Header */}
              <div className={`flex items-center gap-3 mb-4 ${isToday ? 'text-primary' : isEventDay ? 'text-success' : 'text-muted-foreground'}`}>
                <div className={`w-3 h-3 rounded-full ${isToday ? 'bg-primary' : isEventDay ? 'bg-success' : 'bg-muted-foreground'}`} />
                <h3 className="font-medium">
                  {format(parsedDate, "EEEE, dd 'de' MMMM", { locale: ptBR })}
                  {isToday && ' (Hoje)'}
                  {isEventDay && ' (Dia do Churrasco)'}
                </h3>
              </div>
              {/* Tasks */}
              <div className="ml-6 space-y-3">
                {dayTasks?.map((task) => {
                  const categoryInfo = getCategoryInfo(task?.category);
                  const priorityInfo = getPriorityInfo(task?.priority);
                  
                  return (
                    <div
                      key={task?.id}
                      className={`bg-card rounded-lg border p-4 transition-all ${
                        task?.completed ? 'opacity-60 border-success/50' : 'border-border hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <button
                            onClick={() => updateTask(task?.id, 'completed', !task?.completed)}
                            className={`mt-1 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              task?.completed
                                ? 'bg-success border-success text-success-foreground'
                                : 'border-border hover:border-primary'
                            }`}
                          >
                            {task?.completed && <Icon name="Check" size={12} />}
                          </button>
                          
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Icon name={categoryInfo?.icon} size={16} className={categoryInfo?.color} />
                              <span className="text-sm font-medium text-muted-foreground">
                                {task?.time}
                              </span>
                              <span className={`text-xs px-2 py-1 rounded ${priorityInfo?.color}`}>
                                {priorityInfo?.label}
                              </span>
                            </div>
                            
                            <h4 className={`font-medium mb-1 ${task?.completed ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                              {task?.title}
                            </h4>
                            
                            {task?.description && (
                              <p className="text-sm text-muted-foreground mb-2">
                                {task?.description}
                              </p>
                            )}
                            
                            {task?.assignedTo && (
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Icon name="User" size={12} />
                                <span>Responsável: {task?.assignedTo}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeTask(task?.id)}
                          className="text-destructive hover:bg-destructive/10 shrink-0"
                        >
                          <Icon name="Trash2" size={14} />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Connector Line */}
              {date !== sortedDates?.[sortedDates?.length - 1] && (
                <div className="absolute left-1.5 top-8 bottom-0 w-px bg-border" />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-primary/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="Calendar" size={20} className="text-primary" />
            <div>
              <p className="text-sm text-muted-foreground">Total Tarefas</p>
              <p className="text-xl font-bold text-primary">{tasks?.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-success/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="CheckCircle" size={20} className="text-success" />
            <div>
              <p className="text-sm text-muted-foreground">Concluídas</p>
              <p className="text-xl font-bold text-success">{completedCount}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-warning/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="Clock" size={20} className="text-warning" />
            <div>
              <p className="text-sm text-muted-foreground">Próximas</p>
              <p className="text-xl font-bold text-warning">{upcomingTasks?.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-secondary/10 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="TrendingUp" size={20} className="text-secondary" />
            <div>
              <p className="text-sm text-muted-foreground">Progresso</p>
              <p className="text-xl font-bold text-secondary">
                {tasks?.length > 0 ? Math.round((completedCount / tasks?.length) * 100) : 0}%
              </p>
            </div>
          </div>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={generateDefaultTimeline}
          iconName="Zap"
          disabled={!barbecueDate || !barbecueTime}
        >
          Cronograma Sugerido
        </Button>
        
        <Button
          variant="outline"
          onClick={() => setShowAddForm(!showAddForm)}
          iconName="Plus"
        >
          Nova Tarefa
        </Button>
        
        <Button
          variant="outline"
          onClick={() => {
            const allCompleted = tasks?.every(task => task?.completed);
            const updated = tasks?.map(task => ({ ...task, completed: !allCompleted }));
            setTasks(updated);
            onUpdate(updated);
          }}
          iconName="CheckSquare"
          disabled={tasks?.length === 0}
        >
          {tasks?.every(task => task?.completed) ? 'Desmarcar Todas' : 'Marcar Todas'}
        </Button>
      </div>
      {/* Add Task Form */}
      {showAddForm && (
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-foreground">Nova Tarefa</h3>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowAddForm(false)}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Input
              label="Título da Tarefa"
              value={newTask?.title}
              onChange={(e) => setNewTask(prev => ({ ...prev, title: e?.target?.value }))}
              placeholder="Ex: Comprar carne"
              required
            />
            
            <Input
              label="Descrição"
              value={newTask?.description}
              onChange={(e) => setNewTask(prev => ({ ...prev, description: e?.target?.value }))}
              placeholder="Detalhes da tarefa..."
            />
            
            <Input
              label="Data"
              type="date"
              value={newTask?.date}
              onChange={(e) => setNewTask(prev => ({ ...prev, date: e?.target?.value }))}
              required
            />
            
            <Input
              label="Horário"
              type="time"
              value={newTask?.time}
              onChange={(e) => setNewTask(prev => ({ ...prev, time: e?.target?.value }))}
              required
            />
            
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Categoria</label>
              <select
                value={newTask?.category}
                onChange={(e) => setNewTask(prev => ({ ...prev, category: e?.target?.value }))}
                className="w-full h-10 px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {categories?.map(cat => (
                  <option key={cat?.value} value={cat?.value}>{cat?.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Prioridade</label>
              <select
                value={newTask?.priority}
                onChange={(e) => setNewTask(prev => ({ ...prev, priority: e?.target?.value }))}
                className="w-full h-10 px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {priorities?.map(pri => (
                  <option key={pri?.value} value={pri?.value}>{pri?.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button
              onClick={addTask}
              disabled={!newTask?.title?.trim() || !newTask?.date || !newTask?.time}
              iconName="Plus"
              iconPosition="left"
            >
              Adicionar Tarefa
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowAddForm(false)}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}
      {/* Timeline */}
      <div>
        <h3 className="font-medium text-foreground mb-4">Cronograma</h3>
        
        {tasks?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Calendar" size={48} className="mx-auto mb-2 opacity-50" />
            <p>Nenhuma tarefa no cronograma</p>
            <p className="text-sm">
              {barbecueDate && barbecueTime 
                ? 'Clique em "Cronograma Sugerido" para começar' 
                : 'Defina a data e horário do churrasco primeiro'
              }
            </p>
          </div>
        ) : (
          renderTimeline()
        )}
      </div>
    </div>
  );
};

export default PlanningTimeline;